import {
  AsyncLabelLoaders,
  AsyncSuggestionsLoaders,
} from '@ardoq/query-builder';
import {
  getLoadComponentTypeNameSuggestionsAsync,
  getLoadReferenceTypeNameSuggestionsAsync,
  getLoadUserSuggestionsAsync,
} from '@ardoq/suggestions-api';
import { perspectiveOptionsOperations } from './perspectiveOptionsOperations';
import { BasicSelectOption, SelectOptionWithDescription } from './types';
import { referenceOptionOperations } from 'models/utils/referenceOptionOperations';
import { StubReferenceWithTypeName, StubWorkspace } from '@ardoq/data-model';
import { ArdoqId } from '@ardoq/api-types';
import {
  componentApi,
  organizationApi,
  referenceApi,
  workspaceApi,
} from '@ardoq/api';
import {
  caseInsensitiveStringIncludes,
  isArdoqError,
} from '@ardoq/common-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';

type GetPerspectivesSuggestionsLoadersOptions = {
  workspaceFilterOptions: BasicSelectOption[];
  tagOptions: BasicSelectOption[];
  parentComponentOptions: SelectOptionWithDescription[];
  componentOptionsWithCurrentComponent: SelectOptionWithDescription[];
  componentKeyOptions: BasicSelectOption[];
  referenceOptions: BasicSelectOption[];
  relatedReferences: StubReferenceWithTypeName[];
  relatedComponentLabelsById: Map<string, string>;
  relatedWorkspaces: StubWorkspace[];
  componentNameOptions: BasicSelectOption[];
  organizationId: string | null;
  i18nLocale: I18nLocale;
};

const getUnknownLabel = (i18nLocale: I18nLocale) =>
  t(i18nLocale, 'perspectiveSidebar:filtersTab.unknown', 'Unknown');

const getUnknownNameLabel = (id: string, i18nLocale: I18nLocale) =>
  t(
    i18nLocale,
    'perspectiveSidebar:filtersTab.unknownNamed',
    { id },
    '{{id}} (unknown name)'
  );

export const getPerspectivesSuggestionsLoaders = ({
  workspaceFilterOptions,
  tagOptions,
  parentComponentOptions,
  componentOptionsWithCurrentComponent,
  componentKeyOptions,
  referenceOptions,
  relatedReferences,
  relatedComponentLabelsById,
  relatedWorkspaces,
  componentNameOptions,
  organizationId,
  i18nLocale,
}: GetPerspectivesSuggestionsLoadersOptions): {
  asyncSuggestionsLoaders: AsyncSuggestionsLoaders;
  asyncLabelLoaders: AsyncLabelLoaders;
} => ({
  asyncSuggestionsLoaders: {
    loadComponentSuggestionsAsync: async (typedPhrase: string) => {
      return componentNameOptions.filter(({ label }) =>
        caseInsensitiveStringIncludes(label, typedPhrase)
      );
    },
    loadUserSuggestionsAsync: getLoadUserSuggestionsAsync(),
    loadComponentSuggestionsIncludingCurrentComponentAsync: async (
      typedPhrase: string
    ) => {
      return componentOptionsWithCurrentComponent.filter(
        ({ label, description }) =>
          caseInsensitiveStringIncludes(label, typedPhrase) ||
          (description &&
            caseInsensitiveStringIncludes(description, typedPhrase))
      );
    },
    loadComponentKeySuggestionsAsync: async (typedPhrase: string) => {
      return componentKeyOptions.filter(({ label }) =>
        caseInsensitiveStringIncludes(label, typedPhrase)
      );
    },
    loadParentComponentSuggestionsAsync: async (typedPhrase: string) => {
      return parentComponentOptions.filter(({ label }) =>
        caseInsensitiveStringIncludes(label, typedPhrase)
      );
    },
    loadWorkspaceSuggestionsAsync: async (typedPhrase: string) =>
      workspaceFilterOptions.filter(({ label }) =>
        caseInsensitiveStringIncludes(label, typedPhrase)
      ),
    loadTagSuggestionsAsync: async (typedPhrase: string) =>
      tagOptions.filter(({ label }) =>
        caseInsensitiveStringIncludes(label, typedPhrase)
      ),
    loadReferenceOIDSuggestionsAsync: async () => referenceOptions,
    loadComponentTypeNameSuggestionsAsync:
      getLoadComponentTypeNameSuggestionsAsync(),
    loadReferenceTypeNameSuggestionsAsync:
      getLoadReferenceTypeNameSuggestionsAsync(),
  },
  asyncLabelLoaders: {
    getComponentKeyLabelAsync: async (key: string) => {
      if (perspectiveOptionsOperations.isCurrentComponentOption(key)) {
        return perspectiveOptionsOperations.getCurrentComponentLabel();
      }
      return key;
    },
    getComponentLabelAsync: async (id: string) => {
      if (perspectiveOptionsOperations.isParentNoneOption(id)) {
        return perspectiveOptionsOperations.getParentNoneLabel();
      }
      if (perspectiveOptionsOperations.isCurrentComponentOption(id)) {
        return perspectiveOptionsOperations.getCurrentComponentLabel();
      }

      const componentName = relatedComponentLabelsById.get(id);
      if (componentName) {
        return componentName;
      }

      const component = await componentApi.fetch(id);
      if (isArdoqError(component)) {
        return getUnknownNameLabel(id, i18nLocale);
      }
      return component.name;
    },
    getWorkspaceLabelAsync: async (id: string) => {
      const workspace = relatedWorkspaces.find(
        workspace => workspace._id === id
      );
      if (workspace) {
        return workspace.name;
      }

      const result = await workspaceApi.fetch(id);
      if (isArdoqError(result)) {
        return getUnknownNameLabel(id, i18nLocale);
      }
      return result.name;
    },
    getUserLabelAsync: async (userId: string) => {
      const user = await organizationApi.fetchUserById(userId, organizationId);
      if (isArdoqError(user)) {
        return getUnknownNameLabel(userId, i18nLocale);
      }
      return `${user.name} (${user.email})`;
    },
    getReferenceLabelAsync: async (id: ArdoqId) => {
      const reference = relatedReferences.find(
        reference => reference._id === id
      );

      if (reference) {
        const sourceComponentName = relatedComponentLabelsById.get(
          reference.source
        );
        const targetComponentName = relatedComponentLabelsById.get(
          reference.target
        );

        return referenceOptionOperations.toReferenceLabelWithDisplayTextOrTypeAndComponentNames(
          {
            reference,
            // 'Unknown' should not happen as long as database contains consistent data
            sourceComponentName:
              sourceComponentName ?? getUnknownLabel(i18nLocale),
            targetComponentName:
              targetComponentName ?? getUnknownLabel(i18nLocale),
          }
        );
      }

      const result = await referenceApi.fetch(id);
      if (isArdoqError(result)) {
        return getUnknownNameLabel(id, i18nLocale);
      }
      if (result.displayText) {
        return result.displayText;
      }

      const { sourceName, targetName, typeName } = result.ardoq;

      return referenceOptionOperations.toReferenceLabelWithDisplayTextOrTypeAndComponentNames(
        {
          reference: {
            ...result,
            typeName: typeName ?? getUnknownLabel(i18nLocale),
          },
          sourceComponentName: sourceName ?? getUnknownLabel(i18nLocale),
          targetComponentName: targetName ?? getUnknownLabel(i18nLocale),
        }
      );
    },
  },
});
